import { Card } from "@/components/ui/card";
import { Users } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Comment {
  text: string;
  created_at: string;
}

interface Commenter {
  name: string;
  comments: number;
  recentComments: Comment[];
}

interface ActiveCommentersProps {
  commenters: Commenter[];
}

export const ActiveCommenters = ({ commenters }: ActiveCommentersProps) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // Show relative time for recent comments
    if (diffInSeconds < 60) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    // Show full date for older comments
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="h-full bg-zinc-800/20 border-zinc-700">
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 text-white">
          <Users className="w-5 h-5" />
          Active Commenters
        </h3>
        <div className="space-y-4">
          {commenters.length === 0 ? (
            <div className="text-zinc-400">No active commenters yet</div>
          ) : (
            <TooltipProvider>
              {commenters.map((commenter, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm py-2"
                >
                  <span className="text-zinc-300">{commenter.name}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-violet-400 cursor-default">
                        {commenter.comments} comments
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[300px]">
                      <div className="space-y-3">
                        <p className="font-medium text-sm">Recent Comments:</p>
                        {commenter.recentComments?.slice(0, 2).map((comment, idx) => (
                          <div key={idx} className="text-sm">
                            <p className="text-zinc-300 line-clamp-2">{comment.text}</p>
                            <p className="text-zinc-500 text-xs mt-1">{formatDate(comment.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
            </TooltipProvider>
          )}
        </div>
      </div>
    </Card>
  );
};
